import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";
import { DealReward } from "src/dm/interfaces/DealReward";

/**
 * Read the deal's single reward (DEAL_REWARD_PROMO row). DealReward is 1:1
 * with Deal — the legacy code path is
 * `DealReward.GetByDealId(dealId)` which returns a single object, NOT an
 * array (verified at `ContentsAndActions.ascx.cs:161`). The new API
 * exposes this as `GET /dm/V1/deals/{dealId}/reward` (singular).
 *
 * Returns `null` when the deal has no reward yet (the API returns 404 in
 * that case — we map it to `null` so callers can branch on
 * "missing vs error" cleanly). Any other error is re-thrown.
 */
export const useDealReward = (dealId: number | undefined) =>
  useQuery({
    queryKey: ["deal-reward", dealId],
    enabled: dealId !== undefined && dealId > 0,
    queryFn: async (): Promise<DealReward | null> => {
      try {
        const response = await apiClient.get<DealReward>(
          `${APIS.Deals}/${dealId}/reward`
        );
        return response.data;
      } catch (err) {
        // 404 is the documented "no reward yet" signal — return null so the
        // SPA renders the "Create Reward" CTA rather than a generic error.
        if (err instanceof AxiosError && err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
  });
